// ========================================
// なし属性カードの固有効果
// ========================================

import { applyTemporaryAttackReduction } from '../temporaryAttack';
import {
  conditionalDamage,
  searchCard,
  drawCards,
  healLife,
  modifyAttack,
  getPlayerContext,
  applyStatusToAllOpponentMonsters,
} from '../effectHelpers';
import { hasCategory } from '../../utils/helpers';
import { STATUS_EFFECT_TYPES, statusEffectEngine } from '../statusEffects';
import type { CardEffectRegistry } from './index';
import type { MonsterInstance, CardWithId, CardData, PlayerNumber } from '../../types';

/**
 * なし属性カードの固有効果
 *
 * 主に鉄槍騎士団関連のカードを実装
 */
export const neutralCardEffects: CardEffectRegistry = {
  /**
   * C0000196: 援軍の呼び声
   * デッキからコスト3以下のモンスター1体を場に召喚（コスト不要）。
   * このターン中、そのモンスターは攻撃できず召喚時効果無効。
   */
  C0000196: (skillText, context) => {
    const { addLog, setPendingDeckReview, summoningSickness = true } = context;
    const { myDeck, myField, setMyDeck, setMyField } = getPlayerContext(context);

    // 空きスロットを確認
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('援軍の呼び声: 場が満杯で召喚できません', 'info');
      return false;
    }

    // デッキからコスト3以下のモンスターを検索
    const validCards = myDeck.filter(card =>
      card.type === 'monster' && card.cost <= 3
    );

    if (validCards.length === 0) {
      addLog('援軍の呼び声: デッキにコスト3以下のモンスターがいません', 'info');
      return false;
    }

    // デッキ選択UIを表示
    setPendingDeckReview({
      message: 'コスト3以下のモンスターを1体選択してください',
      cards: validCards,
      count: 1,
      callback: (selectedCards: CardWithId[]) => {
        if (selectedCards.length === 0) {
          addLog('援軍の呼び声: キャンセルしました', 'info');
          return;
        }

        const selectedCard = selectedCards[0];

        // デッキから除去
        setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

        // フィールドに召喚（攻撃不可、召喚時効果無効のフラグ付き）
        setMyField(prev => {
          const newField = [...prev];
          const newSlotIndex = newField.findIndex(slot => slot === null);
          if (newSlotIndex !== -1) {
            newField[newSlotIndex] = {
              ...selectedCard,
              currentHp: selectedCard.hp ?? 0,
              currentAttack: selectedCard.attack ?? 0,
              maxHp: selectedCard.hp ?? 0,
              canAttack: !summoningSickness, // 召喚酔い設定に依存
              charges: [],
              statusEffects: [],
              usedSkillThisTurn: false,
              owner: context.currentPlayer,
              // 召喚時効果無効フラグ（トリガー発火を抑制）
              summonEffectNegated: true,
            };
          }
          return newField;
        });

        addLog(`援軍の呼び声: ${selectedCard.name}を援軍として召喚！（このターン攻撃不可、召喚時効果無効）`, 'info');
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000219: 団長プニリーヌ・ソフティア
   * 【常時】場にいる間、［蛮族］モンスターの攻撃力300アップ（continuousEffectsで実装済み）
   * 【エンドフェイズ時】HPを500回復（cardTriggersで実装済み）
   * 基本技：デッキから《鉄槍騎士団》モンスター1体を場に召喚できる（コスト必要）
   */
  C0000219: (skillText, context) => {
    const { addLog, setPendingDeckReview, summoningSickness = false } = context;
    const { myDeck, setMyDeck, myField, setMyField, myActiveSP, setMyActiveSP, setMyRestedSP, currentPlayer } = getPlayerContext(context);

    // 基本技：《鉄槍騎士団》モンスターを召喚
    if (context.skillType === 'basic') {
      // 空きスロットを確認
      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('場に空きがありません', 'info');
        return false;
      }

      // デッキから《鉄槍騎士団》モンスターを検索
      const validCards = myDeck.filter(card =>
        card.type === 'monster' && card.name && card.name.includes('鉄槍騎士団')
      );

      if (validCards.length === 0) {
        addLog('デッキに《鉄槍騎士団》モンスターがいません', 'info');
        return false;
      }

      setPendingDeckReview({
        message: '召喚する《鉄槍騎士団》モンスターを選択',
        cards: validCards,
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards.length === 0) {
            addLog('キャンセルしました', 'info');
            return;
          }

          const selectedCard = selectedCards[0];

          // コストを確認
          if (myActiveSP < selectedCard.cost) {
            addLog(`SPが足りません（必要: ${selectedCard.cost}、現在: ${myActiveSP}）`, 'info');
            return;
          }

          // コストを支払う
          setMyActiveSP(prev => prev - selectedCard.cost);
          setMyRestedSP(prev => prev + selectedCard.cost);

          // デッキから除去
          setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

          // 場に召喚
          setMyField(prev => {
            const newField = [...prev];
            const slot = newField.findIndex(s => s === null);
            if (slot !== -1) {
              newField[slot] = {
                ...selectedCard,
                currentHp: selectedCard.hp ?? 0,
                maxHp: selectedCard.hp ?? 0,
                currentAttack: selectedCard.attack ?? 0,
                canAttack: !summoningSickness, // 召喚酔い設定に依存
                charges: [],
                statusEffects: [],
                usedSkillThisTurn: false,
                owner: currentPlayer,
              };
            }
            return newField;
          });

          addLog(`デッキから${selectedCard.name}を召喚した（コスト${selectedCard.cost}支払い）`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }
    return false;
  },

  /**
   * C0000220: 鉄槍騎士団のゲスール
   * 【エンドフェイズ時】《プニリーヌ》モンスターが場にいる場合、相手モンスター全体に500ダメージ（cardTriggersで実装済み）
   * 基本技：手札を1枚捨てることで相手モンスター1体の攻撃力を0にする
   */
  C0000220: (skillText, context) => {
    const { addLog, setPendingHandSelection, setPendingMonsterTarget } = context;
    const { myHand, setMyHand, setMyGraveyard, opponentField, setOpponentField } = getPlayerContext(context);

    // 基本技：手札1枚捨てて攻撃力0化
    if (context.skillType === 'basic') {
      if (myHand.length === 0) {
        addLog('手札がありません', 'info');
        return false;
      }

      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return false;
      }

      // 手札選択
      setPendingHandSelection({
        message: '捨てる手札を1枚選択',
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (!selectedCards || selectedCards.length === 0) return;
          const discardedCard = selectedCards[0];

          // 手札から除去して墓地へ
          setMyHand(prev => prev.filter(c => c.uniqueId !== discardedCard.uniqueId));
          setMyGraveyard(prev => [...prev, discardedCard]);
          addLog(`${discardedCard.name}を捨てた`, 'info');

          // 相手モンスター選択
          setPendingMonsterTarget({
            message: '攻撃力を0にするモンスターを選択',
            targetPlayer: 'opponent',
            callback: (selectedIndex: number) => {
              setOpponentField(prev => {
                const newField = [...prev];
                if (newField[selectedIndex]) {
                  newField[selectedIndex] = {
                    ...newField[selectedIndex],
                    currentAttack: 0,
                  };
                  addLog(`${newField[selectedIndex].name}の攻撃力を0にした`, 'info');
                }
                return newField;
              });
            },
            selectionOwner: context.currentPlayer,
          });
        },
      });
      return true;
    }
    return false;
  },

  /**
   * C0000221: 鉄槍騎士団のマグラ
   * 基本技：《プニリーヌ》モンスターが場にいる場合、相手の手札1枚をランダムに墓地に送る。
   */
  C0000221: (skillText, context) => {
    const { addLog } = context;
    const { myField, opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // プニリーヌがいるか確認
      const hasPuniline = myField.some(m => m && m.name && m.name.includes('プニリーヌ'));

      if (!hasPuniline) {
        addLog('マグラの基本技: 場に《プニリーヌ》モンスターがいません', 'info');
        return false;
      }

      if (opponentHand.length === 0) {
        addLog('マグラの基本技: 相手の手札がありません', 'info');
        return false;
      }

      // ランダムに1枚破壊
      const randomIndex = Math.floor(Math.random() * opponentHand.length);
      const discardedCard = opponentHand[randomIndex];

      setOpponentHand(prev => prev.filter((_, i) => i !== randomIndex));
      setOpponentGraveyard(prev => [...prev, discardedCard]);

      addLog(`マグラの基本技: 相手の${discardedCard.name}を墓地に送った！`, 'damage');
      return true;
    }

    return false;
  },

  /**
   * C0000223: 鉄槍騎士団のゴロツキ
   * 基本技：デッキから《鉄槍》魔法かフィールドカード1枚を手札に加える。
   */
  C0000223: (skillText, context) => {
    const { addLog } = context;
    const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 「鉄槍」を含む魔法またはフィールドカードを検索
      const foundCard = myDeck.find(card =>
        (card.type === 'magic' || card.type === 'field') &&
        card.name && card.name.includes('鉄槍')
      );

      if (!foundCard) {
        addLog('ゴロツキの基本技: デッキに《鉄槍》魔法/フィールドカードがありません', 'info');
        return false;
      }

      // デッキから除去して手札に加える
      setMyDeck(prev => prev.filter(c => c.uniqueId !== foundCard.uniqueId));
      setMyHand(prev => [...prev, foundCard]);

      addLog(`ゴロツキの基本技: デッキから「${foundCard.name}」を手札に加えた！`, 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000222: 鉄槍騎士団のウィア
   * 【常時】《プニリーヌ》モンスターが場にいるときこのカードの攻撃力を500アップ（continuousEffectsで実装済み）
   * 基本技：自分のライフを500減らすことでデッキから《プニリーヌ・ソフティア》を手札に加える
   */
  C0000222: (skillText, context) => {
    const { addLog } = context;
    const { myDeck, setMyDeck, setMyHand, setMyLife } = getPlayerContext(context);

    // 基本技：ライフ500支払いでプニリーヌサーチ
    if (context.skillType === 'basic') {
      const foundCard = myDeck.find(card =>
        card.name && card.name.includes('プニリーヌ・ソフティア')
      );

      if (!foundCard) {
        addLog('デッキに《プニリーヌ・ソフティア》がいません', 'info');
        return false;
      }

      // ライフを500減らす
      setMyLife(prev => prev - 500);
      addLog('ライフを500支払った', 'damage');

      // デッキから除去して手札に加える
      setMyDeck(prev => prev.filter(c => c.uniqueId !== foundCard.uniqueId));
      setMyHand(prev => [...prev, foundCard]);

      addLog(`デッキから「${foundCard.name}」を手札に加えた`, 'info');
      return true;
    }
    return false;
  },

  /**
   * C0000224: 鉄槍騎士団の奴隷プニリーヌ
   * 【召喚時】場にいる［蛮族］モンスターの数×600ダメージを相手モンスター全体と相手プレイヤーに与える（cardTriggersで実装済み）
   * 【エンドフェイズ時】自分のライフを300減らす（cardTriggersで実装済み）
   * 基本技：相手モンスターの攻撃力を500ダウン
   */
  C0000224: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 基本技：攻撃力500ダウン
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return false;
      }

      setPendingMonsterTarget({
        message: '攻撃力を500下げるモンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          setOpponentField(prev => {
            const newField = [...prev];
            if (newField[selectedIndex]) {
              newField[selectedIndex] = {
                ...newField[selectedIndex],
                currentAttack: Math.max(0, newField[selectedIndex].currentAttack - 500),
              };
              addLog(`${newField[selectedIndex].name}の攻撃力を500下げた（現在: ${newField[selectedIndex].currentAttack}）`, 'info');
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }
    return false;
  },

  /**
   * C0000225: 鉄槍の奉仕
   * 《プニリーヌ》モンスターが場にいる場合、［蛮族］モンスター1体の攻撃力2倍（ターン終了時まで）。
   * 自分のライフを500減らす。
   */
  C0000225: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, setMyLife } = getPlayerContext(context);

    // プニリーヌがいるか確認
    const hasPuniline = myField.some(m => m && m.name && m.name.includes('プニリーヌ'));

    if (!hasPuniline) {
      addLog('鉄槍の奉仕: 場に《プニリーヌ》モンスターがいません', 'info');
      return false;
    }

    // 蛮族モンスターを検索
    const barbarianTargets = myField
      .map((m, i) => ({ monster: m, index: i }))
      .filter(({ monster }) => monster && hasCategory(monster, '蛮族'));

    if (barbarianTargets.length === 0) {
      addLog('鉄槍の奉仕: 場に［蛮族］モンスターがいません', 'info');
      return false;
    }

    // ターゲット選択
    setPendingMonsterTarget({
      message: '攻撃力を2倍にする［蛮族］モンスターを選択',
      validTargets: barbarianTargets.map(t => t.index),
      targetPlayer: 'self',
      callback: (targetIndex: number) => {
        const target = myField[targetIndex];
        if (!target || !hasCategory(target, '蛮族')) {
          addLog('鉄槍の奉仕: 無効な対象です', 'info');
          return;
        }

        // 攻撃力2倍
        setMyField(prev => {
          const newField = [...prev];
          const targetMonster = newField[targetIndex];
          if (!targetMonster) return newField;
          const currentAtk = targetMonster.currentAttack ?? targetMonster.attack ?? 0;
          newField[targetIndex] = {
            ...targetMonster,
            currentAttack: currentAtk * 2,
            atkBuffEndOfTurn: true, // ターン終了時に元に戻すフラグ
            originalAttackBeforeBuff: currentAtk, // 元の値を保存
          };
          return newField;
        });

        // ライフ500減少
        setMyLife(prev => Math.max(0, prev - 500));

        addLog(`鉄槍の奉仕: ${target.name}の攻撃力が2倍に！自分のライフが500減少`, 'info');
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000226: 順番待ちの苛立ち
   * 【刹那詠唱】相手モンスター1体の攻撃力を2000ダウン。
   * ［蛮族］モンスターが場に3体以上で効果無効化。
   */
  C0000226: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField } = getPlayerContext(context);

    // 蛮族3体以上チェック（自分・相手両方のフィールド）
    const barbarianCount = [...myField, ...opponentField]
      .filter(m => m && hasCategory(m, '蛮族')).length;

    if (barbarianCount >= 3) {
      addLog('順番待ちの苛立ち: ［蛮族］モンスターが3体以上いるため効果無効！', 'info');
      return false;
    }

    // 相手モンスターを検索
    const validTargets = opponentField
      .map((m, i) => ({ monster: m, index: i }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('順番待ちの苛立ち: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // ターゲット選択
    setPendingMonsterTarget({
      message: '攻撃力を2000ダウンする相手モンスターを選択',
      validTargets: validTargets.map(t => t.index),
      targetPlayer: 'opponent',
      callback: (targetIndex: number) => {
        const target = opponentField[targetIndex];
        if (!target) {
          addLog('順番待ちの苛立ち: 無効な対象です', 'info');
          return;
        }

        // 攻撃力2000ダウン（最低0）
        setOpponentField(prev => {
          const newField = [...prev];
          const targetMonster = newField[targetIndex];
          if (!targetMonster) return newField;
          const currentAtk = targetMonster.currentAttack ?? targetMonster.attack ?? 0;
          newField[targetIndex] = {
            ...targetMonster,
            currentAttack: Math.max(0, currentAtk - 2000),
          };
          return newField;
        });

        addLog(`順番待ちの苛立ち: ${target.name}の攻撃力を2000ダウン！`, 'damage');
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000227: 鉄槍の昼餐
   * 墓地の［蛮族］モンスター2体を手札に加える。
   * 《プニリーヌ》モンスターが場にいる場合、自分のライフ1000回復。
   */
  C0000227: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection } = context;
    const { myField, myGraveyard, setMyGraveyard, setMyHand, setMyLife } = getPlayerContext(context);

    // 墓地の蛮族モンスターを検索
    const barbarianCards = myGraveyard.filter(card =>
      card.type === 'monster' && hasCategory(card, '蛮族')
    );

    if (barbarianCards.length === 0) {
      addLog('鉄槍の昼餐: 墓地に［蛮族］モンスターがいません', 'info');
      return false;
    }

    // 回収枚数（最大2枚）
    const recoverCount = Math.min(2, barbarianCards.length);

    // 墓地選択UI
    setPendingGraveyardSelection({
      message: `墓地から［蛮族］モンスターを${recoverCount}体選択してください`,
      cards: barbarianCards,
      count: recoverCount,
      callback: (selectedCards: CardWithId[]) => {
        if (selectedCards.length === 0) {
          addLog('鉄槍の昼餐: キャンセルしました', 'info');
          return;
        }

        // 墓地から除去して手札に加える
        const selectedIds = new Set(selectedCards.map(c => c.uniqueId));
        setMyGraveyard(prev => prev.filter(c => !selectedIds.has(c.uniqueId)));
        setMyHand(prev => [...prev, ...selectedCards]);

        addLog(`鉄槍の昼餐: ${selectedCards.map(c => c.name).join('、')}を手札に回収！`, 'info');

        // プニリーヌがいれば回復
        const hasPuniline = myField.some(m => m && m.name && m.name.includes('プニリーヌ'));
        if (hasPuniline) {
          setMyLife(prev => prev + 1000);
          addLog('鉄槍の昼餐: 《プニリーヌ》効果でライフ1000回復！', 'heal');
        }
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000228: 鉄槍の睡眠罠
   * 次ターン終了時まで相手モンスター全体の攻撃力を1000ダウンし、「眠り」状態にする。
   * 自分の《プニリーヌ》モンスターはこのターン攻撃できない。
   */
  C0000228: (skillText, context) => {
    const { addLog } = context;
    const { myField, opponentField, setMyField, setOpponentField } = getPlayerContext(context);

    // 相手フィールドにモンスターがいるか確認
    const hasOpponentMonsters = opponentField.some(m => m !== null);
    if (!hasOpponentMonsters) {
      addLog('鉄槍の睡眠罠: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // 相手モンスター全体に攻撃力1000ダウン＋眠り状態を適用
    setOpponentField(prev => {
      return prev.map(monster => {
        if (!monster) return null;
        const currentAtk = monster.currentAttack ?? monster.attack ?? 0;
        return {
          ...monster,
          currentAttack: Math.max(0, currentAtk - 1000),
          // 次ターン終了時まで有効（2ターン持続）
          atkDebuffDuration: 2,
          atkDebuffAmount: 1000,
          // 眠り状態を付与
          statusEffects: [
            ...(monster.statusEffects || []),
            {
              type: STATUS_EFFECT_TYPES.SLEEP,
              duration: 2, // 次ターン終了時まで
              removeChance: 0.5, // 50%で解除
              source: 'C0000228',
              sourceName: '鉄槍の睡眠罠',
            },
          ],
        };
      });
    });

    // 自分のプニリーヌは攻撃不可
    const hasPuniline = myField.some(m => m && m.name && m.name.includes('プニリーヌ'));
    if (hasPuniline) {
      setMyField(prev => {
        return prev.map(monster => {
          if (!monster) return null;
          if (monster.name && monster.name.includes('プニリーヌ')) {
            return {
              ...monster,
              canAttack: false,
            };
          }
          return monster;
        });
      });
      addLog(`鉄槍の睡眠罠: 相手モンスター全体の攻撃力を1000ダウンし、眠り状態に！（自分のプニリーヌは攻撃不可）`, 'damage');
    } else {
      addLog(`鉄槍の睡眠罠: 相手モンスター全体の攻撃力を1000ダウンし、眠り状態に！`, 'damage');
    }

    return true;
  },

  /**
   * C0000189: 旅の助っ人
   * 基本技：デッキからコスト2以下の「なし」属性魔法カード1枚を手札に加える。
   */
  C0000189: (skillText, context) => {
    const { addLog } = context;
    const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // コスト2以下の「なし」属性魔法カードを検索
      const foundCard = myDeck.find(card =>
        card.type === 'magic' &&
        card.attribute === 'なし' &&
        card.cost <= 2
      );

      if (!foundCard) {
        addLog('旅の助っ人の基本技: デッキにコスト2以下の「なし」属性魔法カードがありません', 'info');
        return false;
      }

      // デッキから除去して手札に加える
      setMyDeck(prev => prev.filter(c => c.uniqueId !== foundCard.uniqueId));
      setMyHand(prev => [...prev, foundCard]);

      addLog(`旅の助っ人の基本技: 「${foundCard.name}」を手札に加えた！`, 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000190: 守りの歩哨
   * 基本技：自分のモンスター1体のHPを1000回復。
   */
  C0000190: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 回復対象となるモンスターを検索（HPが減っているもの）
      const validTargets = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null && monster.currentHp < (monster.maxHp ?? monster.hp ?? 0));

      if (validTargets.length === 0) {
        addLog('守りの歩哨の基本技: 回復対象のモンスターがいません', 'info');
        return false;
      }

      if (validTargets.length === 1) {
        // 1体しかいない場合は自動選択
        const targetIndex = validTargets[0].index;
        setMyField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const maxHp = target.maxHp ?? target.hp ?? 0;
            const healAmount = Math.min(1000, maxHp - target.currentHp);
            newField[targetIndex] = {
              ...target,
              currentHp: target.currentHp + healAmount,
            };
            addLog(`守りの歩哨の基本技: ${target.name}のHPを${healAmount}回復！`, 'heal');
          }
          return newField;
        });
        return true;
      }

      // 複数対象がいる場合は選択
      setPendingMonsterTarget({
        message: 'HPを1000回復する自分のモンスターを選択',
        validTargets: validTargets.map(t => t.index),
        targetPlayer: 'self',
        callback: (targetIndex: number) => {
          setMyField(prev => {
            const newField = [...prev];
            const target = newField[targetIndex];
            if (target) {
              const maxHp = target.maxHp ?? target.hp ?? 0;
              const healAmount = Math.min(1000, maxHp - target.currentHp);
              newField[targetIndex] = {
                ...target,
                currentHp: target.currentHp + healAmount,
              };
              addLog(`守りの歩哨の基本技: ${target.name}のHPを${healAmount}回復！`, 'heal');
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    return false;
  },

  /**
   * C0000206: 反逆の剣士
   * 基本技：SP1を払い、自分または相手の場にいる攻撃力2000以上のモンスター1体を破壊。
   */
  C0000206: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, monsterIndex } = context;
    const {
      myField, opponentField, setMyField, setOpponentField,
      myActiveSP, setMyActiveSP, setMyRestedSP,
      setMyGraveyard, setOpponentGraveyard,
    } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // SP1があるか確認
      if (myActiveSP < 1) {
        addLog('反逆の剣士の基本技: SPが足りません', 'info');
        return false;
      }

      // 攻撃力2000以上のモンスターを検索（自分側 + 相手側）
      const selfTargets = myField
        .map((m, idx) => ({ monster: m, index: idx, side: 'self' as const }))
        .filter(({ monster, index }) =>
          monster !== null &&
          index !== monsterIndex && // 自分自身は除く
          (monster.currentAttack ?? monster.attack ?? 0) >= 2000
        );

      const opponentTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx, side: 'opponent' as const }))
        .filter(({ monster }) =>
          monster !== null &&
          (monster.currentAttack ?? monster.attack ?? 0) >= 2000
        );

      const allTargets = [...selfTargets, ...opponentTargets];

      if (allTargets.length === 0) {
        addLog('反逆の剣士の基本技: 攻撃力2000以上のモンスターがいません', 'info');
        return false;
      }

      // SP1を支払う
      setMyActiveSP(prev => prev - 1);
      setMyRestedSP(prev => prev + 1);

      // ターゲット選択
      setPendingMonsterTarget({
        message: '破壊する攻撃力2000以上のモンスターを選択（自分/相手どちらも可）',
        targetPlayer: 'both',
        callback: (targetIndex: number, targetSide: string) => {
          // targetSideがない場合、選択結果から判定
          const isOpponentTarget = targetSide === 'opponent' ||
            (targetSide === undefined && opponentTargets.some(t => t.index === targetIndex));

          if (isOpponentTarget) {
            const target = opponentField[targetIndex];
            if (target && (target.currentAttack ?? target.attack ?? 0) >= 2000) {
              setOpponentField(prev => {
                const newField = [...prev];
                newField[targetIndex] = null;
                return newField;
              });
              setOpponentGraveyard(prev => [...prev, target]);
              addLog(`反逆の剣士の基本技: ${target.name}を破壊！`, 'damage');
            }
          } else {
            const target = myField[targetIndex];
            if (target && (target.currentAttack ?? target.attack ?? 0) >= 2000) {
              setMyField(prev => {
                const newField = [...prev];
                newField[targetIndex] = null;
                return newField;
              });
              setMyGraveyard(prev => [...prev, target]);
              addLog(`反逆の剣士の基本技: ${target.name}を破壊！`, 'damage');
            }
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    return false;
  },

  // ========================================
  // 汎用魔法カード
  // ========================================

  /**
   * C0000207: 知恵の代償
   * 自分のライフを500減らし、デッキから2枚ドローする。
   * このターン、自分は魔法カードを追加で発動できない。
   */
  C0000207: (skillText, context) => {
    const { addLog, setP1MagicBlocked, setP2MagicBlocked, currentPlayer } = context;
    const { setMyLife } = getPlayerContext(context);

    // ライフを500減らす
    setMyLife(prev => Math.max(0, prev - 500));
    addLog('知恵の代償: ライフを500支払った', 'damage');

    // 2枚ドロー
    drawCards(context, 2);

    // このターン魔法カード発動不可（自分のみ）
    if (currentPlayer === 1) {
      setP1MagicBlocked(true);
    } else {
      setP2MagicBlocked(true);
    }
    addLog('知恵の代償: このターン追加の魔法カードは発動できない', 'info');

    return true;
  },

  /**
   * C0000195: 交換の手札
   * 自分の手札を1枚デッキに戻し、デッキから2枚ドローする。
   * （このターン、自分は追加ドローできない）
   */
  C0000195: (skillText, context) => {
    const { addLog, setPendingHandSelection } = context;
    const { myHand, myDeck, setMyHand, setMyDeck } = getPlayerContext(context);

    if (myHand.length === 0) {
      addLog('交換の手札: 手札がありません', 'info');
      return false;
    }

    // 手札選択UI
    setPendingHandSelection({
      message: 'デッキに戻す手札を1枚選択してください',
      count: 1,
      callback: (selectedCards: CardWithId[]) => {
        if (selectedCards.length === 0) {
          addLog('交換の手札: キャンセルしました', 'info');
          return;
        }

        const returnCard = selectedCards[0];

        // 手札から除去
        setMyHand(prev => prev.filter(c => c.uniqueId !== returnCard.uniqueId));

        // デッキに戻してシャッフル
        setMyDeck(prev => {
          const newDeck = [...prev, returnCard];
          // シャッフル
          for (let i = newDeck.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
          }
          return newDeck;
        });

        addLog(`交換の手札: ${returnCard.name}をデッキに戻した`, 'info');

        // 2枚ドロー（追加ドローフラグを設定）
        drawCards(context, 2);

        // TODO: 追加ドロー制限フラグを実装する場合はここで設定
        addLog('交換の手札: このターン追加ドローは不可', 'info');
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000194: 急ごしらえの罠
   * 【刹那詠唱】相手モンスターの攻撃力をターン終了時まで500下げ、
   * そのモンスターに500ダメージを与える。
   */
  C0000194: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手モンスターがいるか確認
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('急ごしらえの罠: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    const applyEffect = (targetIndex: number) => {
      setOpponentField(prev => {
        const newField = [...prev];
        if (newField[targetIndex]) {
          const target = newField[targetIndex];
          const newHp = target.currentHp - 500;

          if (newHp <= 0) {
            // 破壊
            setOpponentGraveyard(p => [...p, target]);
            addLog(`急ごしらえの罠: ${target.name}の攻撃力-500、500ダメージで破壊！`, 'damage');
            newField[targetIndex] = null;
          } else {
            newField[targetIndex] = {
              ...applyTemporaryAttackReduction(target, 500),
              currentHp: newHp,
            };
            addLog(`急ごしらえの罠: ${target.name}の攻撃力-500、500ダメージ！`, 'damage');
          }
        }
        return newField;
      });
    };

    if (opponentMonsters.length === 1) {
      applyEffect(opponentMonsters[0].index);
    } else {
      setPendingMonsterTarget({
        message: '対象の相手モンスターを選択してください',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
    }

    return true;
  },

  /**
   * C0000198: 逆転の足音
   * 墓地のモンスター1体を自分の場に戻す（攻撃力は半分）。
   * 次のターン、自分のライフを500減らす。
   */
  C0000198: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection, currentPlayer, setP1SpReduction, setP2SpReduction, summoningSickness = false } = context;
    const { myGraveyard, myField, setMyGraveyard, setMyField, setMyLife } = getPlayerContext(context);

    // 墓地のモンスターを検索
    const graveyardMonsters = myGraveyard.filter(c => c.type === 'monster');

    if (graveyardMonsters.length === 0) {
      addLog('逆転の足音: 墓地にモンスターがいません', 'info');
      return false;
    }

    // 空きスロットを確認
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('逆転の足音: 場が満杯です', 'info');
      return false;
    }

    // 墓地選択UI
    setPendingGraveyardSelection({
      message: '蘇生するモンスターを選択してください',
      cards: graveyardMonsters,
      count: 1,
      callback: (selectedCards: CardWithId[]) => {
        if (selectedCards.length === 0) {
          addLog('逆転の足音: キャンセルしました', 'info');
          return;
        }

        const targetCard = selectedCards[0];

        // 墓地から除去
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));

        // 攻撃力半分で蘇生
        const halfAttack = Math.floor((targetCard.attack ?? 0) / 2);
        setMyField(prev => {
          const newField = [...prev];
          const slot = newField.findIndex(s => s === null);
          if (slot !== -1) {
            newField[slot] = {
              ...targetCard,
              currentHp: targetCard.hp ?? 0,
              currentAttack: halfAttack,
              maxHp: targetCard.hp ?? 0,
              canAttack: !summoningSickness, // 召喚酔い設定に依存
              charges: [],
              statusEffects: [],
              usedSkillThisTurn: false,
              owner: currentPlayer,
              // 次のターン開始時にライフ減少フラグ
              pendingLifeCost: 500,
            };
          }
          return newField;
        });

        addLog(`逆転の足音: ${targetCard.name}を攻撃力${halfAttack}で蘇生！（次のターン開始時にライフ-500）`, 'info');

        // 次のターン開始時のライフコストはターン開始処理で実装が必要
        // 簡易実装: 即座にライフを減らす方式（代替案）
        // setMyLife(prev => Math.max(0, prev - 500));
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000193: 一時の休息
   * 自分のライフを800回復する。
   */
  C0000193: (skillText, context) => {
    const { addLog } = context;
    const { setMyLife } = getPlayerContext(context);

    setMyLife(prev => prev + 800);
    addLog('一時の休息: ライフを800回復！', 'heal');
    return true;
  },

  /**
   * C0000197: 力の均衡
   * 場にいる攻撃力2000以上のモンスター1体の攻撃力をターン終了時まで半分にする。
   * このターン中に、そのモンスターが破壊されると、自分のSPを1回復。
   */
  C0000197: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setMyField, setOpponentField, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

    // 攻撃力2000以上のモンスターを検索（両フィールド）
    const myTargets = myField
      .map((m, idx) => ({ monster: m, index: idx, side: 'self' }))
      .filter(({ monster }) => monster && (monster.currentAttack ?? monster.attack ?? 0) >= 2000);

    const oppTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx, side: 'opponent' }))
      .filter(({ monster }) => monster && (monster.currentAttack ?? monster.attack ?? 0) >= 2000);

    const allTargets = [...myTargets, ...oppTargets];

    if (allTargets.length === 0) {
      addLog('力の均衡: 攻撃力2000以上のモンスターがいません', 'info');
      return false;
    }

    // ターゲット選択
    setPendingMonsterTarget({
      message: '攻撃力を半分にするモンスターを選択（攻撃力2000以上）',
      targetPlayer: 'both',
      callback: (targetIndex: number, targetSide: string) => {
        const isOpponentTarget = targetSide === 'opponent' ||
          (targetSide === undefined && oppTargets.some(t => t.index === targetIndex));

        const targetField = isOpponentTarget ? opponentField : myField;
        const setTargetField = isOpponentTarget ? setOpponentField : setMyField;
        const target = targetField[targetIndex];

        if (!target || (target.currentAttack ?? target.attack ?? 0) < 2000) {
          addLog('力の均衡: 無効な対象です', 'info');
          return;
        }

        const currentAtk = target.currentAttack ?? target.attack ?? 0;
        const halfAtk = Math.floor(currentAtk / 2);

        setTargetField(prev => {
          const newField = [...prev];
          const targetMonster = newField[targetIndex];
          if (!targetMonster) return newField;
          newField[targetIndex] = {
            ...targetMonster,
            currentAttack: halfAtk,
            // 破壊時SP回復フラグ
            equilibriumTarget: true,
            equilibriumOwner: context.currentPlayer,
          };
          return newField;
        });

        addLog(`力の均衡: ${target.name}の攻撃力を${currentAtk}→${halfAtk}に半減！（破壊時SP回復）`, 'damage');
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000202: 嵐の突風
   * 場に存在するフィールドカード1枚を破壊する。自分のSPトークンを一つ破壊する。
   */
  C0000202: (skillText, context) => {
    const { addLog, currentPlayer } = context;
    const {
      myFieldCard, opponentFieldCard,
      setMyFieldCard, setOpponentFieldCard,
      setMyGraveyard, setOpponentGraveyard,
      myActiveSP, setMyActiveSP, myRestedSP, setMyRestedSP,
    } = getPlayerContext(context);

    // フィールドカードがあるか確認
    if (!myFieldCard && !opponentFieldCard) {
      addLog('嵐の突風: 場にフィールドカードがありません', 'info');
      return false;
    }

    // SPが1以上あるか確認（トークンを破壊する必要がある）
    if (myActiveSP === 0 && myRestedSP === 0) {
      addLog('嵐の突風: SPトークンがありません', 'info');
      return false;
    }

    // フィールドカードを破壊（相手優先）
    if (opponentFieldCard) {
      const uniqueId = 'uniqueId' in opponentFieldCard ? String(opponentFieldCard.uniqueId) : `field-${Date.now()}`;
      setOpponentGraveyard(prev => [...prev, { ...opponentFieldCard, uniqueId }]);
      setOpponentFieldCard(null);
      addLog(`嵐の突風: 相手の${opponentFieldCard.name}を破壊！`, 'damage');
    } else if (myFieldCard) {
      const uniqueId = 'uniqueId' in myFieldCard ? String(myFieldCard.uniqueId) : `field-${Date.now()}-self`;
      setMyGraveyard(prev => [...prev, { ...myFieldCard, uniqueId }]);
      setMyFieldCard(null);
      addLog(`嵐の突風: 自分の${myFieldCard.name}を破壊！`, 'damage');
    }

    // SPトークンを1つ破壊（アクティブ優先）
    if (myActiveSP > 0) {
      setMyActiveSP(prev => prev - 1);
    } else {
      setMyRestedSP(prev => prev - 1);
    }
    addLog('嵐の突風: SPトークンを1つ破壊', 'damage');

    return true;
  },

  /**
   * C0000203: 地盤崩し
   * 場に存在するフィールドカード1枚を破壊し、そのカードのプレイヤーに300ダメージを与える。
   */
  C0000203: (skillText, context) => {
    const { addLog } = context;
    const {
      myFieldCard, opponentFieldCard,
      setMyFieldCard, setOpponentFieldCard,
      setMyGraveyard, setOpponentGraveyard,
      setMyLife, setOpponentLife,
    } = getPlayerContext(context);

    // フィールドカードがあるか確認
    if (!myFieldCard && !opponentFieldCard) {
      addLog('地盤崩し: 場にフィールドカードがありません', 'info');
      return false;
    }

    // フィールドカードを破壊（相手優先）し、持ち主にダメージ
    if (opponentFieldCard) {
      const uniqueId = 'uniqueId' in opponentFieldCard ? String(opponentFieldCard.uniqueId) : `field-${Date.now()}`;
      setOpponentGraveyard(prev => [...prev, { ...opponentFieldCard, uniqueId }]);
      setOpponentFieldCard(null);
      setOpponentLife(prev => Math.max(0, prev - 300));
      addLog(`地盤崩し: 相手の${opponentFieldCard.name}を破壊し、相手に300ダメージ！`, 'damage');
    } else if (myFieldCard) {
      const uniqueId = 'uniqueId' in myFieldCard ? String(myFieldCard.uniqueId) : `field-${Date.now()}-self`;
      setMyGraveyard(prev => [...prev, { ...myFieldCard, uniqueId }]);
      setMyFieldCard(null);
      setMyLife(prev => Math.max(0, prev - 300));
      addLog(`地盤崩し: 自分の${myFieldCard.name}を破壊し、自分に300ダメージ！`, 'damage');
    }

    return true;
  },

  /**
   * C0000204: 崩れゆく礎
   * 自分の手札を1枚墓地に捨てなければならない。場に存在するフィールドカード1枚を破壊する。
   */
  C0000204: (skillText, context) => {
    const { addLog, setPendingHandSelection } = context;
    const {
      myHand, myFieldCard, opponentFieldCard,
      setMyFieldCard, setOpponentFieldCard,
      setMyHand, setMyGraveyard, setOpponentGraveyard,
    } = getPlayerContext(context);

    // 手札があるか確認
    if (myHand.length === 0) {
      addLog('崩れゆく礎: 手札がありません', 'info');
      return false;
    }

    // フィールドカードがあるか確認
    if (!myFieldCard && !opponentFieldCard) {
      addLog('崩れゆく礎: 場にフィールドカードがありません', 'info');
      return false;
    }

    // 手札選択UI
    setPendingHandSelection({
      message: '墓地に捨てる手札を1枚選択してください',
      count: 1,
      callback: (selectedCards: CardWithId[]) => {
        if (selectedCards.length === 0) {
          addLog('崩れゆく礎: キャンセルしました', 'info');
          return;
        }

        const discardCard = selectedCards[0];

        // 手札から除去して墓地に送る
        setMyHand(prev => prev.filter(c => c.uniqueId !== discardCard.uniqueId));
        setMyGraveyard(prev => [...prev, discardCard]);
        addLog(`崩れゆく礎: ${discardCard.name}を墓地に捨てた`, 'info');

        // フィールドカードを破壊（相手優先）
        if (opponentFieldCard) {
          const uniqueId = 'uniqueId' in opponentFieldCard ? String(opponentFieldCard.uniqueId) : `field-${Date.now()}`;
          setOpponentGraveyard(prev => [...prev, { ...opponentFieldCard, uniqueId }]);
          setOpponentFieldCard(null);
          addLog(`崩れゆく礎: 相手の${opponentFieldCard.name}を破壊！`, 'damage');
        } else if (myFieldCard) {
          const uniqueId = 'uniqueId' in myFieldCard ? String(myFieldCard.uniqueId) : `field-${Date.now()}-self`;
          setMyGraveyard(prev => [...prev, { ...myFieldCard, uniqueId }]);
          setMyFieldCard(null);
          addLog(`崩れゆく礎: 自分の${myFieldCard.name}を破壊！`, 'damage');
        }
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000208: 逆転の賭け
   * 自分のライフが相手より少ない場合発動できる。
   * 自分のライフを100にして、自分のSPトークンを2個増やす。
   */
  C0000208: (skillText, context) => {
    const { addLog } = context;
    const { myLife, opponentLife, setMyLife, setMyActiveSP } = getPlayerContext(context);

    // 発動条件チェック
    if (myLife >= opponentLife) {
      addLog('逆転の賭け: 自分のライフが相手以上のため発動できません', 'info');
      return false;
    }

    // ライフを100にする
    setMyLife(100);
    addLog(`逆転の賭け: ライフを${myLife}→100に！`, 'damage');

    // SPトークンを2個増やす
    setMyActiveSP(prev => prev + 2);
    addLog('逆転の賭け: SPトークンを2個獲得！', 'info');

    return true;
  },

  /**
   * C0000209: 即興の連携【刹那詠唱】
   * 場にいる自分のモンスター1体の攻撃力をターン終了時まで800アップする。
   * このターン中、そのモンスターが戦闘で相手モンスターにダメージを与えた場合、その相手モンスターを破壊する。
   */
  C0000209: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 自分のモンスターがいるか確認
    const myMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (myMonsters.length === 0) {
      addLog('即興の連携: 場にモンスターがいません', 'info');
      return false;
    }

    const applyEffect = (targetIndex: number) => {
      setMyField(prev => {
        const newField = [...prev];
        const monster = newField[targetIndex];
        if (monster) {
          const currentAtk = monster.currentAttack ?? monster.attack ?? 0;
          newField[targetIndex] = {
            ...monster,
            currentAttack: currentAtk + 800,
            // 戦闘ダメージで相手破壊フラグ
            combatDestroy: true,
          };
          addLog(`即興の連携: ${monster.name}の攻撃力+800！（戦闘ダメージで相手破壊）`, 'info');
        }
        return newField;
      });
    };

    if (myMonsters.length === 1) {
      applyEffect(myMonsters[0].index);
    } else {
      setPendingMonsterTarget({
        message: '攻撃力+800するモンスターを選択',
        targetPlayer: 'self',
        callback: (targetIndex: number) => {
          applyEffect(targetIndex);
        },
        selectionOwner: context.currentPlayer,
      });
    }

    return true;
  },

  /**
   * C0000210: 静寂の帳【刹那詠唱】
   * このターン、場にいる全てのモンスターの効果を無効化する。
   * 発動後、自分の次のターン開始時、自分のライフを600減らす。
   */
  C0000210: (skillText, context) => {
    const { addLog } = context;
    const { setMyField, setOpponentField } = getPlayerContext(context);

    // 両フィールドのモンスターにSILENCE状態異常を付与
    setMyField(prev => {
      return prev.map(monster => {
        if (!monster) return null;
        statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.SILENCE, {
          duration: 0, // このターン終了時まで
          source: 'C0000210',
          sourceName: '静寂の帳',
        });
        return monster;
      });
    });

    setOpponentField(prev => {
      return prev.map(monster => {
        if (!monster) return null;
        statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.SILENCE, {
          duration: 0, // このターン終了時まで
          source: 'C0000210',
          sourceName: '静寂の帳',
        });
        return monster;
      });
    });

    addLog('静寂の帳: このターン、全モンスターの効果を無効化！（次のターン開始時ライフ-600）', 'info');

    // 次のターン開始時のライフコストはターン開始処理で実装が必要
    // TODO: pendingSilenceCostフラグまたはトリガーシステムで対応

    return true;
  },

  /**
   * C0000432: 棘根の罠
   * 相手モンスター1体の攻撃力を次のターン終了時まで600ダウン。
   * このターン終了時、そのモンスターが場にいる場合、そのプレイヤーに400ダメージを与える。
   */
  C0000432: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 相手モンスターがいるか確認
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('棘根の罠: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    const applyEffect = (targetIndex: number) => {
      setOpponentField(prev => {
        const newField = [...prev];
        const target = newField[targetIndex];
        if (target) {
          const currentAtk = target.currentAttack ?? target.attack ?? 0;
          const newAtk = Math.max(0, currentAtk - 600);

          newField[targetIndex] = {
            ...target,
            currentAttack: newAtk,
            // 次のターン終了時までの攻撃力減少（2ターン持続）
            thornTrapReduction: 600,
            thornTrapTurnsRemaining: 2,
            // このターン終了時に400ダメージフラグ
            thornTrapDamage: 400,
          };
          addLog(`棘根の罠: ${target.name}の攻撃力-600！（ターン終了時に400ダメージ）`, 'damage');
        }
        return newField;
      });
    };

    if (opponentMonsters.length === 1) {
      applyEffect(opponentMonsters[0].index);
    } else {
      setPendingMonsterTarget({
        message: '対象の相手モンスターを選択してください',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
    }

    return true;
  },

  /**
   * C0000367: 秘島の風声（魔法カード）
   * 自分の手札の《ヴォランティス》モンスター1体の召喚コストを2軽減し、
   * そのカードと同名カードをデッキから手札に加える。
   */
  C0000367: (skillText, context) => {
    const { addLog, setPendingHandSelection } = context;
    const { myHand, setMyHand, myDeck, setMyDeck } = getPlayerContext(context);

    // 手札の《ヴォランティス》モンスターを検索
    const volantisList = myHand.filter(card =>
      card.type === 'monster' && card.name && card.name.includes('ヴォランティス')
    );

    if (volantisList.length === 0) {
      addLog('秘島の風声: 手札に《ヴォランティス》モンスターがいません', 'info');
      return false;
    }

    // 手札選択
    const selectTarget = (selectedCard: CardWithId) => {
      // コスト2軽減フラグを付与
      setMyHand(prev => prev.map(card => {
        if (card.uniqueId === selectedCard.uniqueId) {
          return {
            ...card,
            tempCostModifier: (card.tempCostModifier ?? 0) - 2,
            tempCostModifierSource: '秘島の風声',
          };
        }
        return card;
      }));
      addLog(`秘島の風声: ${selectedCard.name}の召喚コストを2軽減！`, 'info');

      // 同名カードをデッキから検索
      const sameNameCard = myDeck.find(card => card.name === selectedCard.name);
      if (sameNameCard) {
        setMyDeck(prev => prev.filter(c => c.uniqueId !== sameNameCard.uniqueId));
        setMyHand(prev => [...prev, sameNameCard]);
        addLog(`秘島の風声: デッキから「${sameNameCard.name}」を手札に加えた！`, 'info');
      } else {
        addLog(`秘島の風声: デッキに「${selectedCard.name}」の同名カードがありません`, 'info');
      }
    };

    if (volantisList.length === 1) {
      selectTarget(volantisList[0]);
    } else if (setPendingHandSelection) {
      setPendingHandSelection({
        message: 'コストを2軽減する《ヴォランティス》モンスターを選択',
        cards: volantisList,
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            selectTarget(selectedCards[0]);
          }
        },
        selectionOwner: context.currentPlayer,
      });
    } else {
      selectTarget(volantisList[0]);
    }

    return true;
  },

  /**
   * C0000381: 虹羽密林の生命網（魔法カード）【刹那詠唱】
   * 自分の墓地の《虹羽密林》モンスター2体を手札に戻す、
   * 場に《虹羽の微脈》がある場合、さらに手札からコスト3以下《虹羽密林》モンスター1枚を召喚し、
   * その攻撃力をターン終了時まで1000アップ。
   */
  C0000381: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection, setPendingHandSelection, registerCardTriggers, continuousEffectEngine, summoningSickness = false } = context;
    const { myGraveyard, setMyGraveyard, setMyHand, myHand, myField, setMyField, myFieldCard, currentPlayer } = getPlayerContext(context);

    // 墓地の《虹羽密林》モンスターを検索
    const nijibaMitsurinMonsters = myGraveyard.filter(card =>
      card.type === 'monster' && card.name && card.name.includes('虹羽密林')
    );

    if (nijibaMitsurinMonsters.length === 0) {
      addLog('虹羽密林の生命網: 墓地に《虹羽密林》モンスターがいません', 'info');
      return false;
    }

    // 回収枚数（最大2枚）
    const recoverCount = Math.min(2, nijibaMitsurinMonsters.length);

    // 墓地選択UI
    setPendingGraveyardSelection({
      message: `墓地から《虹羽密林》モンスターを${recoverCount}体選択してください`,
      cards: nijibaMitsurinMonsters,
      count: recoverCount,
      callback: (selectedCards: CardWithId[]) => {
        if (selectedCards.length === 0) {
          addLog('虹羽密林の生命網: キャンセルしました', 'info');
          return;
        }

        // 墓地から除去して手札に加える
        const selectedIds = new Set(selectedCards.map(c => c.uniqueId));
        setMyGraveyard(prev => prev.filter(c => !selectedIds.has(c.uniqueId)));
        setMyHand(prev => [...prev, ...selectedCards]);

        addLog(`虹羽密林の生命網: ${selectedCards.map(c => c.name).join('、')}を手札に回収！`, 'info');

        // 《虹羽の微脈》がフィールドカードにあるか確認
        const hasNijibaMimyaku = myFieldCard && myFieldCard.name && myFieldCard.name.includes('虹羽の微脈');

        if (hasNijibaMimyaku) {
          // 空きスロットを確認
          const emptySlotIndex = myField.findIndex(slot => slot === null);
          if (emptySlotIndex === -1) {
            addLog('虹羽密林の生命網: 場が満杯で追加召喚できません', 'info');
            return;
          }

          // 手札からコスト3以下の《虹羽密林》モンスターを検索
          // Note: 今回手札に加えたカードも含む
          const updatedHand = [...myHand, ...selectedCards];
          const summonCandidates = updatedHand.filter(card =>
            card.type === 'monster' &&
            card.name && card.name.includes('虹羽密林') &&
            card.cost <= 3
          );

          if (summonCandidates.length > 0 && setPendingHandSelection) {
            setPendingHandSelection({
              message: '追加召喚するコスト3以下の《虹羽密林》モンスターを選択',
              cards: summonCandidates,
              count: 1,
              callback: (summonCards: CardWithId[]) => {
                if (summonCards && summonCards.length > 0) {
                  const cardToSummon = summonCards[0];
                  // 手札から除去
                  setMyHand(prev => prev.filter(c => c.uniqueId !== cardToSummon.uniqueId));

                  // 場に召喚（攻撃力+1000）
                  const slot = myField.findIndex(s => s === null);
                  if (slot !== -1) {
                    const summonedMonster = {
                      ...cardToSummon,
                      currentHp: cardToSummon.hp ?? 0,
                      currentAttack: (cardToSummon.attack ?? 0) + 1000, // ATK+1000
                      maxHp: cardToSummon.hp ?? 0,
                      canAttack: !summoningSickness, // 召喚酔い設定に依存
                      charges: [],
                      statusEffects: [],
                      usedSkillThisTurn: false,
                      owner: currentPlayer as PlayerNumber,
                      attackBuffUntilEndOfTurn: 1000,
                    };

                    setMyField(prev => {
                      const newField = [...prev];
                      const newSlot = newField.findIndex(s => s === null);
                      if (newSlot !== -1) {
                        newField[newSlot] = summonedMonster;
                      }
                      return newField;
                    });

                    // トリガー登録
                    if (registerCardTriggers) {
                      registerCardTriggers(summonedMonster, currentPlayer, slot);
                    }
                    // 常時効果登録
                    if (continuousEffectEngine) {
                      continuousEffectEngine.register(summonedMonster, currentPlayer);
                    }

                    addLog(`虹羽密林の生命網: 《虹羽の微脈》効果で${cardToSummon.name}を召喚！（攻撃力+1000）`, 'info');
                  }
                }
              },
              selectionOwner: context.currentPlayer,
            });
          }
        }
      },
    });

    return true;
  },

  /**
   * C0000382: 虹羽の覚醒脈（魔法カード）
   * 自分の場に《虹羽密林》モンスターが2体以上いる場合に発動可能。
   * 自分の場の《虹羽密林》モンスターを全て墓地に送り、その数×500ダメージを自分に与える。
   * その後、自分のデッキ・手札・墓地から《ヴォランティス》モンスター1体を召喚（召喚コスト無視）。
   * この効果で召喚したモンスターは、このターン中攻撃できず、次のターン終了時まで効果が無効化されない。
   * このカードを発動したターン、自分は他のモンスターを場に出せない。
   */
  C0000382: (skillText, context) => {
    const { addLog, setPendingDeckReview, registerCardTriggers, continuousEffectEngine, summoningSickness = true } = context;
    const {
      myField, setMyField, setMyGraveyard, setMyLife,
      myDeck, setMyDeck, myHand, setMyHand, myGraveyard, currentPlayer,
    } = getPlayerContext(context);

    // 発動条件: 《虹羽密林》モンスターが2体以上
    const nijibaMitsurinMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.name && monster.name.includes('虹羽密林'));

    if (nijibaMitsurinMonsters.length < 2) {
      addLog('虹羽の覚醒脈: 《虹羽密林》モンスターが2体以上必要です', 'info');
      return false;
    }

    // 空きスロットがあるか確認（リリース後に空くので現在の状態は問わない）
    const monsterCount = nijibaMitsurinMonsters.length;

    // 《虹羽密林》モンスターを全て墓地に送る
    const sacrificedMonsters = nijibaMitsurinMonsters
      .map(({ monster }) => monster)
      .filter((m): m is MonsterInstance => m !== null);
    setMyField(prev => prev.map((m, idx) => {
      if (nijibaMitsurinMonsters.some(({ index }) => index === idx)) {
        return null;
      }
      return m;
    }));
    setMyGraveyard(prev => [...prev, ...sacrificedMonsters as CardWithId[]]);
    addLog(`虹羽の覚醒脈: ${sacrificedMonsters.map(m => m.name).join('、')}を墓地に送った！`, 'info');

    // 自分にダメージ
    const selfDamage = monsterCount * 500;
    setMyLife(prev => Math.max(0, prev - selfDamage));
    addLog(`虹羽の覚醒脈: 自分に${selfDamage}ダメージ！`, 'damage');

    // デッキ・手札・墓地から《ヴォランティス》モンスターを検索
    const volantisFromDeck = myDeck.filter(c => c.type === 'monster' && c.name && c.name.includes('ヴォランティス'));
    const volantisFromHand = myHand.filter(c => c.type === 'monster' && c.name && c.name.includes('ヴォランティス'));
    const volantisFromGraveyard = myGraveyard.filter(c => c.type === 'monster' && c.name && c.name.includes('ヴォランティス'));

    const allVolantis = [
      ...volantisFromDeck.map(c => ({ card: c, source: 'deck' })),
      ...volantisFromHand.map(c => ({ card: c, source: 'hand' })),
      ...volantisFromGraveyard.map(c => ({ card: c, source: 'graveyard' })),
    ];

    if (allVolantis.length === 0) {
      addLog('虹羽の覚醒脈: 《ヴォランティス》モンスターがどこにもいません', 'info');
      return true;
    }

    // 選択UI
    const summonVolantis = (selected: { card: CardWithId; source: string }) => {
      const { card, source } = selected;

      // 取得元から削除
      if (source === 'deck') {
        setMyDeck(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
      } else if (source === 'hand') {
        setMyHand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
      } else if (source === 'graveyard') {
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
      }

      // 場に召喚
      const summonedMonster = {
        ...card,
        currentHp: card.hp ?? 0,
        currentAttack: card.attack ?? 0,
        maxHp: card.hp ?? 0,
        canAttack: !summoningSickness, // 召喚酔い設定に依存
        charges: [],
        statusEffects: [],
        usedSkillThisTurn: false,
        owner: currentPlayer as PlayerNumber,
        effectImmuneUntilNextTurnEnd: true, // 次のターン終了時まで効果無効化されない
        uniqueId: `${card.id}_${Date.now()}_${Math.random()}`,
      };

      setMyField(prev => {
        const newField = [...prev];
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          newField[slot] = summonedMonster;

          // トリガー登録
          if (registerCardTriggers) {
            registerCardTriggers(summonedMonster, currentPlayer, slot);
          }
          // 常時効果登録
          if (continuousEffectEngine) {
            continuousEffectEngine.register(summonedMonster, currentPlayer);
          }
        }
        return newField;
      });

      addLog(`虹羽の覚醒脈: ${source === 'deck' ? 'デッキ' : source === 'hand' ? '手札' : '墓地'}から${card.name}を召喚！（このターン攻撃不可、効果無効化されない）`, 'info');
      addLog('虹羽の覚醒脈: このターン、他のモンスターを場に出せない', 'info');

      // 追加召喚制限フラグを設定（実際の制限処理は別途必要）
    };

    if (allVolantis.length === 1) {
      summonVolantis(allVolantis[0]);
    } else if (setPendingDeckReview) {
      setPendingDeckReview({
        message: '召喚する《ヴォランティス》モンスターを選択（デッキ/手札/墓地から）',
        cards: allVolantis.map(v => v.card),
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            const selectedCard = selectedCards[0];
            const selected = allVolantis.find(v => v.card.uniqueId === selectedCard.uniqueId);
            if (selected) {
              summonVolantis(selected);
            }
          }
        },
        selectionOwner: context.currentPlayer,
      });
    } else {
      summonVolantis(allVolantis[0]);
    }

    return true;
  },

  /**
   * C0000239: 散歩の思い出（魔法カード）
   * 自分のモンスター1体のHPを500回復する。
   */
  C0000239: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 自分の場にモンスターがいるか確認
    const myMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (myMonsters.length === 0) {
      addLog('散歩の思い出: 自分の場にモンスターがいません', 'info');
      return false;
    }

    const applyHeal = (selectedIndex: number) => {
      setMyField(prev => prev.map((m, idx) => {
        if (idx === selectedIndex && m) {
          const currentHp = m.currentHp ?? m.hp ?? 0;
          const maxHp = m.maxHp ?? m.hp ?? currentHp;
          const newHp = Math.min(maxHp, currentHp + 500);
          addLog(`散歩の思い出: ${m.name}のHPを500回復！（HP: ${currentHp} → ${newHp}）`, 'heal');
          return { ...m, currentHp: newHp };
        }
        return m;
      }));
    };

    // 1体のみの場合は自動選択
    if (myMonsters.length === 1) {
      applyHeal(myMonsters[0].index);
      return true;
    }

    // 複数の場合はターゲット選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'HPを500回復するモンスターを選択',
        targetPlayer: 'self',
        callback: (selectedIndex: number) => {
          applyHeal(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    applyHeal(myMonsters[0].index);
    return true;
  },

  // ========================================
  // フェイズカード
  // ========================================

  /**
   * C0000383: 虹羽密林の共鳴弦（フェイズカード）
   * 3枚重ね: 自分の場の水、光、炎属性モンスター全ての攻撃力を500アップし、このカードを墓地に送る。
   */
  C0000383: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (skillText.includes('3枚重ね') || skillText.includes('３枚重ね')) {
      // 自分の場の水、光、炎属性モンスター全ての攻撃力を500アップ
      let buffedCount = 0;
      setMyField(prev => {
        return prev.map(monster => {
          if (monster && ['水', '光', '炎'].includes(monster.attribute)) {
            buffedCount++;
            return {
              ...monster,
              currentAttack: (monster.currentAttack ?? monster.attack ?? 0) + 500,
            };
          }
          return monster;
        });
      });

      if (buffedCount > 0) {
        addLog(`虹羽密林の共鳴弦【3枚重ね】: 水/光/炎属性モンスター${buffedCount}体の攻撃力+500！`, 'info');
      } else {
        addLog('虹羽密林の共鳴弦【3枚重ね】: 対象となるモンスターがいません', 'info');
      }

      return true;
    }

    return false;
  },

  // ========================================
  // 【ポイズンウェイル】関連カード
  // ========================================

  /**
   * C0000286: 毒草の調合（なし属性魔法）
   * 自分のライフを600減らし、デッキから【ポイズンウェイル】モンスター1体を手札に加える。
   */
  C0000286: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, setMyHand, setMyLife } = getPlayerContext(context);

    // 自分のライフを600減らす
    setMyLife(prev => prev - 600);
    addLog('毒草の調合: 自分のライフを600減らした！', 'damage');

    // デッキから【ポイズンウェイル】モンスターを探す
    const poisonWeilMonsters = myDeck.filter(c =>
      c.type === 'monster' && hasCategory(c, 'ポイズンウェイル')
    );

    if (poisonWeilMonsters.length === 0) {
      addLog('毒草の調合: デッキに【ポイズンウェイル】モンスターがありません', 'info');
      return true;
    }

    // 複数いる場合は選択UI
    if (poisonWeilMonsters.length > 1 && setPendingDeckReview) {
      setPendingDeckReview({
        cards: poisonWeilMonsters,
        message: '手札に加える【ポイズンウェイル】モンスターを選択',
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            const selectedCard = selectedCards[0];
            setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
            setMyHand(prev => [...prev, selectedCard]);
            addLog(`毒草の調合: 「${selectedCard.name}」を手札に加えた！`, 'info');
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 1体のみの場合は自動選択
    const targetCard = poisonWeilMonsters[0];
    setMyDeck(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
    setMyHand(prev => [...prev, targetCard]);
    addLog(`毒草の調合: 「${targetCard.name}」を手札に加えた！`, 'info');

    return true;
  },

  /**
   * C0000243: ご主人様のおしおき
   * 【刹那詠唱】
   * 相手モンスター1体の攻撃力を0にし、ターン終了時まで行動不能状態にする。
   * 自分の《ご主人様》または《リリカ》モンスター1体の攻撃力を1000アップ。
   */
  C0000243: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, myField, setMyField } = getPlayerContext(context);

    // 相手フィールドにモンスターがいるか確認
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('ご主人様のおしおき: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // 相手モンスター選択後の処理
    const applyPunishment = (targetIndex: number) => {
      setOpponentField(prev => {
        const newField = [...prev];
        if (newField[targetIndex]) {
          // 攻撃力を0にし、行動不能にする
          newField[targetIndex] = {
            ...newField[targetIndex],
            currentAttack: 0,
            atkDebuffedUntilEndOfTurn: true,
            canAttack: false,
            actionDisabledUntilEndOfTurn: true,
          };
          addLog(`${newField[targetIndex].name}の攻撃力が0になり、行動不能状態！`, 'info');
        }
        return newField;
      });

      // 自分の《ご主人様》または《リリカ》を探す
      const goshujinOrLilika = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && monster.name &&
          (monster.name.includes('ご主人様') || monster.name.includes('リリカ')));

      if (goshujinOrLilika.length === 0) {
        addLog('自分の場に《ご主人様》または《リリカ》がいません', 'info');
        return;
      }

      // バフ適用関数
      const applyBuff = (buffIndex: number) => {
        setMyField(prev => {
          const newField = [...prev];
          const monster = newField[buffIndex];
          if (monster) {
            const currentAtk = monster.currentAttack ?? monster.attack ?? 0;
            const newAtk = currentAtk + 1000;
            newField[buffIndex] = {
              ...monster,
              currentAttack: newAtk,
            };
            addLog(`${monster.name}の攻撃力が1000アップ！（${newAtk}）`, 'info');
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (goshujinOrLilika.length === 1) {
        applyBuff(goshujinOrLilika[0].index);
        return;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '攻撃力を1000アップする《ご主人様》または《リリカ》を選択',
          targetPlayer: 'self',
          validTargets: goshujinOrLilika.map(t => t.index),
          callback: (selectedIndex: number) => {
            applyBuff(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
      } else {
        applyBuff(goshujinOrLilika[0].index);
      }
    };

    // 相手モンスターが1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      applyPunishment(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    setPendingMonsterTarget({
      message: '攻撃力を0にする相手モンスターを選択',
      targetPlayer: 'opponent',
      callback: (selectedIndex: number) => {
        applyPunishment(selectedIndex);
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000401: 呪術狩りの傭兵バランド
   * 基本技: 自分の手札1枚を墓地に送り、相手の闇属性モンスター1体のHPを1000減らす（ターンに1度）
   */
  C0000401: (skillText, context) => {
    const { addLog, setPendingHandSelection, setPendingMonsterTarget } = context;
    const { myHand, setMyHand, setMyGraveyard, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 手札が必要
      if (myHand.length === 0) {
        addLog('傭兵バランドの基本技: 手札がありません', 'info');
        return false;
      }

      // 相手の闘属性モンスターを検索
      const darkMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && monster.attribute === '闇');

      if (darkMonsters.length === 0) {
        addLog('傭兵バランドの基本技: 相手フィールドに闇属性モンスターがいません', 'info');
        return false;
      }

      // 手札選択
      setPendingHandSelection({
        message: '墓地に送る手札を1枚選択',
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (!selectedCards || selectedCards.length === 0) {
            addLog('傭兵バランドの基本技: キャンセルしました', 'info');
            return;
          }

          const discardCard = selectedCards[0];

          // 手札から墓地へ
          setMyHand(prev => prev.filter(c => c.uniqueId !== discardCard.uniqueId));
          setMyGraveyard(prev => [...prev, discardCard]);
          addLog(`傭兵バランドの基本技: ${discardCard.name}を墓地に送った`, 'info');

          // 闇属性モンスター選択
          const applyDamage = (targetIndex: number) => {
            setOpponentField(prev => {
              const newField = [...prev];
              const target = newField[targetIndex];
              if (target && target.attribute === '闇') {
                const newHp = target.currentHp - 1000;
                if (newHp <= 0) {
                  // 破壊
                  setOpponentGraveyard(p => [...p, target]);
                  addLog(`傭兵バランドの基本技: ${target.name}にHP1000ダメージで破壊！`, 'damage');
                  newField[targetIndex] = null;
                } else {
                  newField[targetIndex] = {
                    ...target,
                    currentHp: newHp,
                  };
                  addLog(`傭兵バランドの基本技: ${target.name}にHP1000ダメージ！（残HP: ${newHp}）`, 'damage');
                }
              }
              return newField;
            });
          };

          // 1体のみなら自動選択
          if (darkMonsters.length === 1) {
            applyDamage(darkMonsters[0].index);
          } else {
            setPendingMonsterTarget({
              message: 'HPを1000減らす闇属性モンスターを選択',
              targetPlayer: 'opponent',
              validTargets: darkMonsters.map(t => t.index),
              callback: (selectedIndex: number) => {
                applyDamage(selectedIndex);
              },
              selectionOwner: context.currentPlayer,
            });
          }
        },
      });

      return true;
    }

    return false;
  },

  /**
   * C0000441: 斧使いダンベル
   * 基本技: 相手モンスター1体に800ダメージを与える。
   */
  C0000441: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 基本技: 相手モンスター1体に800ダメージ
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('斧使いダンベルの基本技: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      const applyDamage = (targetIndex: number) => {
        const target = opponentField[targetIndex];
        if (!target) return;

        const newHp = target.currentHp - 800;
        addLog(`斧使いダンベルの基本技: ${target.name}に800ダメージ！`, 'damage');

        if (newHp <= 0) {
          addLog(`${target.name}は破壊された！`, 'damage');
          setOpponentGraveyard(prev => [...prev, target]);
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
        } else {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = { ...target, currentHp: newHp };
            return newField;
          });
        }
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyDamage(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '800ダメージを与える相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyDamage(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyDamage(validTargets[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000550: 撃墜命令
   * 飛行を持つモンスター1体を破壊する。
   */
  C0000550: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);
    const { hasHikou } = require('../keywordAbilities');

    // 相手フィールドの飛行モンスターを検索
    const flyingTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null && hasHikou(monster));

    if (flyingTargets.length === 0) {
      addLog('撃墜命令: 相手フィールドに飛行モンスターがいません', 'info');
      return false;
    }

    const destroyTarget = (targetIndex: number) => {
      const target = opponentField[targetIndex];
      if (!target) return;

      // モンスターを破壊
      setOpponentField((prev) => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });
      setOpponentGraveyard((prev) => [...prev, target]);
      addLog(`撃墜命令: 飛行モンスター「${target.name}」を撃墜！`, 'damage');
    };

    // 1体のみの場合は自動選択
    if (flyingTargets.length === 1) {
      destroyTarget(flyingTargets[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    setPendingMonsterTarget({
      message: '撃墜する飛行モンスターを選択',
      targetPlayer: 'opponent',
      validTargets: flyingTargets.map(({ index }) => index),
      callback: (selectedIndex: number) => {
        destroyTarget(selectedIndex);
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },
};
